import { useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  AgendaAppointmentItemDataFragment,
  ConsultType,
  GetTemplateTestRangeItemDocument,
  GetTemplateTestRangeItemQueryVariables,
  TemplateFrequency,
} from '../../types/api/graphql';

const getSkip = (variables: GetTemplateTestRangeItemQueryVariables) => {
  if (
    !variables.healthProviderId ||
    !variables.healthProfessionalId ||
    !variables.templateId ||
    !variables.startDate ||
    !variables.frequency
  ) {
    return true;
  }

  if (variables.frequency !== TemplateFrequency.Once && !variables.endDate) {
    return true;
  }

  return false;
};

export function useTemplateRangeCheck(
  variables: GetTemplateTestRangeItemQueryVariables
) {
  const {
    data: templateRangeCheckData,
    loading: templateRangeCheckLoading,
    error: templateRangeCheckError,
  } = useCacheQuery(GetTemplateTestRangeItemDocument, {
    variables: {
      ...variables,
      endDate:
        variables.frequency !== TemplateFrequency.Once
          ? variables.endDate
          : null,
    },
    skip: getSkip(variables),
  });

  const data = useMemo(() => {
    const appointments = (
      templateRangeCheckData?.templateApplyTest?.existingAppointments ?? []
    ).map(option => option as AgendaAppointmentItemDataFragment);

    if (appointments.length > 0) {
      return {
        appointments,
        hasAppointments: true,
        hasOverwritableItems:
          appointments.filter(x => x.consultType === ConsultType.Else).length >
          0,
      };
    }

    return {
      appointments: [],
      hasAppointments: false,
      hasOverwritableItems: false,
    };
  }, [templateRangeCheckData?.templateApplyTest.existingAppointments]);

  return {
    data,
    loading: templateRangeCheckLoading,
    error: templateRangeCheckError,
  };
}
